import * as React from 'react';

import {
  Button,
  Col,
  Menu,
  Pagination,
  Row,
  SearchField,
  TinyTable,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue } from 'jotai';
import {
  LucideEllipsis,
  LucideMail,
  LucidePhone,
  LucidePlusCircle,
  LucideSettings,
  LucideUserMinus,
  LucideUser,
} from 'lucide-react';

import { ConfirmationDialog } from '../_shared/ConfirmationDialog';

import { useCustomerPortalUserRoleRevokeMutation } from './_graphql/useCustomerPortalUserRoleRevokeMutation';
import {
  ExternalRoleUserRecord,
  useExternalRoleUsersQuery,
} from './_graphql/useExternalRoleUsersQuery';

import { UserStatusBadge } from '~/modules/settings/_components/_shared';
import { $currentRole } from '~/modules/settings/_state/roleForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

type CpRoleUserRowMenuProps = {
  onViewUserSettings: () => void;
  onRemoveFromRole: () => void;
};

const CpRoleUserRowMenu = ({
  onViewUserSettings,
  onRemoveFromRole,
}: CpRoleUserRowMenuProps) => {
  const t = useTranslations();

  const menuItems = React.useMemo(() => {
    const items: Array<{ label: React.ReactNode; onAction: () => void }> = [];

    items.push({
      label: (
        <div className="flex items-center gap-2">
          <LucideSettings className="size-4" />
          {t.customerPortal.roles.details.users.viewUserSettings}
        </div>
      ),
      onAction: onViewUserSettings,
    });

    items.push({
      label: (
        <div className="flex items-center gap-2 text-failure">
          <LucideUserMinus className="size-4" />
          {t.customerPortal.roles.details.users.removeFromRole}
        </div>
      ),
      onAction: onRemoveFromRole,
    });

    return items;
  }, [t, onViewUserSettings, onRemoveFromRole]);

  return (
    <Menu
      trigger={
        <Button
          size="icon"
          icon={<LucideEllipsis />}
          variant="ghost"
          color="neutral"
          className="border-neutral/20"
        />
      }
      items={menuItems}
      menuClassName="@CpRoleUserRowMenu"
    />
  );
};

export const CustomerPortalRoleUsersTab = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const currentRole = useAtomValue($currentRole);

  const { toast } = useToast();
  const revokeMutation = useCustomerPortalUserRoleRevokeMutation();
  const [userToRemove, setUserToRemove] =
    React.useState<ExternalRoleUserRecord | null>(null);

  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const [offset, setOffset] = React.useState(0);
  const [limit, setLimit] = React.useState(20);

  const {
    data: { users: allUsers = [] } = {},
    isLoading,
    error,
  } = useExternalRoleUsersQuery({
    roleId: currentRole?.id ?? '',
    limit: 1000,
    offset: 0,
  });

  const filteredUsers = React.useMemo(() => {
    let result = allUsers;

    if (debouncedSearchTerm) {
      const lowerSearch = debouncedSearchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(lowerSearch) ||
          user.email.toLowerCase().includes(lowerSearch),
      );
    }

    return result;
  }, [allUsers, debouncedSearchTerm]);

  const paginatedUsers = React.useMemo(
    () => filteredUsers.slice(offset, offset + limit),
    [filteredUsers, offset, limit],
  );

  const total = filteredUsers.length;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleViewUserSettings = (user: ExternalRoleUserRecord) => {
    setLocation(`/customer-portal/users/${user.id}` as never);
  };

  const handleRemoveFromRole = (user: ExternalRoleUserRecord) => {
    setUserToRemove(user);
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove) return;
    try {
      await revokeMutation.mutateAsync({
        assignmentId: userToRemove.assignmentId,
      });
      setUserToRemove(null);
      toast({
        title:
          t.customerPortal.roles.details.users.removeFromRoleDialog.success,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t.customerPortal.roles.details.users.removeFromRoleDialog.error,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  const handleRemoveDialogOpenChange = (open: boolean) => {
    if (!revokeMutation.isPending) {
      setUserToRemove(open ? userToRemove : null);
    }
  };

  if (!currentRole) {
    return null;
  }

  return (
    <Col className="@CustomerPortalRoleUsersTab h-full gap-4 p-5">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={t.customerPortal.search.placeholder}
          autoFocus
        />
        <Button className="ml-auto" icon={<LucidePlusCircle />}>
          {t.customerPortal.actions.addRole}
        </Button>
      </Row>

      <TinyTable<ExternalRoleUserRecord>
        rowKey={(row) => row.id}
        selectable
        isLoading={isLoading}
        error={error ?? undefined}
        onRowClick={(row) => handleViewUserSettings(row)}
        columns={[
          {
            key: 'name',
            label: t.customerPortal.users.table.columns.name,
            icon: LucideUser,
            cell: (row) => (
              <Col className="gap-0.5">
                <span>{row.name}</span>
                {row.roles.length > 0 && (
                  <span className="text-xs text-neutral/50">
                    {row.roles.join(', ')}
                  </span>
                )}
              </Col>
            ),
          },
          {
            key: 'contactInfo',
            label: t.customerPortal.users.table.columns.contactInfo,
            icon: LucideMail,
            cell: (row) => (
              <Col className="gap-0.5">
                <Row className="items-center gap-1.5">
                  <LucideMail className="size-3 text-neutral/50" />
                  <span className="truncate">{row.email || '-'}</span>
                </Row>
                <Row className="items-center gap-1.5">
                  <LucidePhone className="size-3 text-neutral/50" />
                  <span>{row.phoneNumber || '-'}</span>
                </Row>
              </Col>
            ),
          },
          {
            key: 'status',
            label: t.customerPortal.users.table.columns.status,
            width: 100,
            cell: (row) => <UserStatusBadge status={row.status} />,
          },
          {
            key: 'actions',
            label: '',
            width: 60,
            preventRowClick: true,
            cell: (row) => (
              <CpRoleUserRowMenu
                onViewUserSettings={() => handleViewUserSettings(row)}
                onRemoveFromRole={() => handleRemoveFromRole(row)}
              />
            ),
          },
        ]}
        data={paginatedUsers}
      />

      {total > 0 && (
        <Row className="justify-end">
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Row>
      )}

      <ConfirmationDialog
        isOpen={userToRemove !== null}
        onOpenChange={handleRemoveDialogOpenChange}
        title={t.customerPortal.roles.details.users.removeFromRoleDialog.title(
          userToRemove?.name ?? '',
        )}
        description={t.customerPortal.roles.details.users.removeFromRoleDialog.description(
          userToRemove?.name ?? '',
        )}
        confirmLabel={
          t.customerPortal.roles.details.users.removeFromRoleDialog.confirm
        }
        cancelLabel={
          t.customerPortal.roles.details.users.removeFromRoleDialog.cancel
        }
        onConfirm={handleRemoveConfirm}
        onCancel={() => handleRemoveDialogOpenChange(false)}
        isLoading={revokeMutation.isPending}
      />
    </Col>
  );
};
